/* eslint-disable unicorn/no-nested-ternary */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DateTime } from 'luxon';

import { RouteSegment } from '@core/enums/route-segment.enum';
import { selectBreakpointActive } from '@core/stores/breakpoint/breakpoint.selectors';
import { selectRouterState } from '@core/stores/router/router.selectors';

import { HealthGradeStoreModelEnum, VehicleOperationalStatusStoreModelEnum } from '@domain/single-site/single-site.enums';

import { SocGraphMtuData } from '@shared/components/soc-graph';
import { SortDirectionStoreModelEnum } from '@shared/enums/sort-direction.enum';
import { formatDateToApiFormat } from '@shared/utils/date';
import { isDefined } from '@shared/utils/is-defined';
import { getPathParamOfRoute } from '@shared/utils/router-state';
import { compareByDateString } from '@shared/utils/sort-utils';
import { wToKw } from '@shared/utils/w-to-kw';
import { whToKwh } from '@shared/utils/wh-to-kwh';

import { selectCurrentSiteTimeZone } from '../../../single-site/store/single-site.selectors';
import { VehicleInformationCardViewModel } from '../components/vehicle-information-card';
import { VehicleLocationCardViewModel } from '../components/vehicle-location-card';
import { VehicleRealTimeDataCardViewModel } from '../components/vehicle-real-time-data-card';
import { VehicleRecentSessionsCardViewModel } from '../components/vehicle-recent-sessions-card';
import { VehicleStateOfChargeCardViewModel } from '../components/vehicle-state-of-charge-card';
import { VehicleDetailsOverviewState, vehicleDetailsOverviewFeatureKey } from './vehicle-details-overview.reducer';

export const selectVehicleDetailsOverviewFeature = createFeatureSelector<VehicleDetailsOverviewState>(vehicleDetailsOverviewFeatureKey);

export const selectVehicleDetails = createSelector(selectVehicleDetailsOverviewFeature, (state) => state.vehicleDetails);
export const selectVehicleDetailsIsLoading = createSelector(selectVehicleDetailsOverviewFeature, (state) => state.vehicleDetailsIsLoading);
export const selectVehicleDetailsError = createSelector(selectVehicleDetailsOverviewFeature, (state) => state.vehicleDetailsError);
export const selectCardsLayout = createSelector(selectVehicleDetailsOverviewFeature, selectBreakpointActive('lg'), (state, lgActive) =>
  lgActive ? state.cardsLayout : state.mobileCardsLayout
);

export const selectVehicleDetailsName = createSelector(selectVehicleDetails, (details) => details?.name);

export const selectVehicleDetailsOverviewVehicleId = createSelector(selectRouterState, (state) => {
  return getPathParamOfRoute(state, 'vehicleId');
});

export const selectVehicleDetailsOverviewSiteId = createSelector(selectRouterState, (state) => {
  return getPathParamOfRoute(state, 'siteId');
});

export const selectVehicleInformationCardVm = createSelector(
  selectVehicleDetails,
  selectVehicleDetailsIsLoading,
  (details, loading): VehicleInformationCardViewModel => ({
    loading,
    imgUrl: details?.evModel.picture ?? undefined,
    make: details?.evModel.make ?? undefined,
    licensePlate: details?.licensePlate,
    model: details?.evModel.model ?? undefined,
    dimensions: details?.evModel.dimensions ?? undefined,
    socketCompatibility: details?.evModel.sockets ?? undefined,
    maxRangeKm: isDefined(details?.evModel.maxRange) ? details.evModel.maxRange : undefined,
    capacityKwh: isDefined(details?.evModel.capacity) ? whToKwh(details.evModel.capacity) : undefined,
    maxChargingPowerKw: isDefined(details?.evModel.maxChargingPower) ? wToKw(details.evModel.maxChargingPower) : undefined,
    minChargingPowerKw: isDefined(details?.evModel.minChargingPower) ? wToKw(details.evModel.minChargingPower) : undefined,
    idTags: details?.idTags,
  })
);

export const selectVehicleLocationCardVm = createSelector(
  selectVehicleDetails,
  selectVehicleDetailsIsLoading,
  (details, loading): VehicleLocationCardViewModel => {
    const socPercentage = isDefined(details?.telematics?.lastSoc) ? details.telematics.lastSoc * 100 : undefined;
    const thresholds = details?.socGraphData?.bandThresholds;
    const socHealthGrade =
      socPercentage === undefined || !thresholds
        ? undefined
        : socPercentage > thresholds[HealthGradeStoreModelEnum.Warning]
          ? HealthGradeStoreModelEnum.Good
          : socPercentage > thresholds[HealthGradeStoreModelEnum.Error]
            ? HealthGradeStoreModelEnum.Warning
            : HealthGradeStoreModelEnum.Error;

    return {
      loading,
      position: details?.telematics?.gpsCoordinate ?? undefined,
      vehicleName: details?.name,
      socPercentage,
      socHealthGrade,
    };
  }
);

export const selectVehicleRealTimeDataCardVm = createSelector(
  selectVehicleDetails,
  selectVehicleDetailsIsLoading,
  (details, loading): VehicleRealTimeDataCardViewModel => {
    const efficiencyWhPerKm =
      details?.telematics?.lastVehicleStatus === VehicleOperationalStatusStoreModelEnum.OnTheRoad &&
      isDefined(details.telematics.efficiency)
        ? details.telematics.efficiency
        : undefined;

    return {
      loading,
      status: details?.telematics?.status,
      lastBatterySocPercentage: isDefined(details?.telematics?.lastSoc) ? details!.telematics!.lastSoc! * 100 : undefined,
      bandThresholds: details?.socGraphData?.bandThresholds,
      lastDataPoint: details?.telematics?.lastDatapointTimestamp,
      estimatedRangeRemaining: isDefined(details?.telematics?.estimatedRangeRemaining)
        ? details.telematics.estimatedRangeRemaining
        : undefined,
      estimatedDistanceFromDepot: isDefined(details?.telematics?.estimatedDistanceFromDepot)
        ? details.telematics.estimatedDistanceFromDepot
        : undefined,
      lastVehicleStatus: details?.telematics?.lastVehicleStatus,
      lastOdometerReading: isDefined(details?.telematics?.odometer) ? details.telematics.odometer : undefined,
      timeOnTheRoadStart:
        details?.telematics?.lastVehicleStatus &&
        [VehicleOperationalStatusStoreModelEnum.OnTheRoad, VehicleOperationalStatusStoreModelEnum.Unknown].includes(
          details.telematics.lastVehicleStatus
        )
          ? details?.telematics?.onTheRoadStartTime
          : undefined,
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      efficiencyKwhPerUnit: isDefined(efficiencyWhPerKm) ? whToKwh(efficiencyWhPerKm, 2) : undefined,
      efficiencyHealthGrade: isDefined(efficiencyWhPerKm) ? (details?.telematics?.efficiencyHealthGrade ?? undefined) : undefined,
    };
  }
);

export const selectVehicleStateOfChargeCardVm = createSelector(
  selectVehicleDetails,
  selectVehicleDetailsIsLoading,
  (details, loading): VehicleStateOfChargeCardViewModel => {
    const socGraphData = details?.socGraphData;
    const socGraphVm =
      socGraphData?.data && socGraphData.data.length > 0
        ? {
            maxBarWidth: 4,
            bandThresholds: socGraphData.bandThresholds,
            data: socGraphData.data.map(
              (item): SocGraphMtuData => ({
                mtu: DateTime.fromISO(item.timestamp).plus({ minutes: 30 }),
                socPercentage: isDefined(item.soc) ? item.soc * 100 : undefined,
                charging: item.charging,
              })
            ),
          }
        : undefined;

    return {
      loading,
      socGraphData: socGraphVm,
    };
  }
);

export const selectRecentSessions = createSelector(selectVehicleDetailsOverviewFeature, (state) => state.recentSessions);
export const selectRecentSessionsIsLoading = createSelector(selectVehicleDetailsOverviewFeature, (state) => state.recentSessionsIsLoading);

const RECENT_SESSIONS_COUNT = 10;

export const selectVehicleRecentSessionsCardVm = createSelector(
  selectRecentSessions,
  selectRecentSessionsIsLoading,
  selectVehicleDetailsOverviewSiteId,
  selectVehicleDetailsOverviewVehicleId,
  selectCurrentSiteTimeZone,
  (sessions, isLoading, siteId, vehicleId, timeZone): VehicleRecentSessionsCardViewModel => {
    const sortedSessions = sessions
      ? [...sessions]
          .sort((a, b) => compareByDateString(a.start, b.start, SortDirectionStoreModelEnum.Desc))
          .slice(0, RECENT_SESSIONS_COUNT)
      : undefined;

    const endDate = timeZone ? DateTime.now().setZone(timeZone) : undefined;
    const start = endDate?.isValid ? formatDateToApiFormat(endDate.minus({ weeks: 1 })) : undefined;
    const end = endDate?.isValid ? formatDateToApiFormat(endDate) : undefined;

    return {
      loading: isLoading,
      sessions: sortedSessions?.map((session) => ({
        id: session.id,
        status: session.status,
        sessionOutcome: session.sessionOutcome,
        start: session.start,
        end: session.end,
        energyKwh: session.energyDeliveredKwh,
        startingSocPercentage: isDefined(session.startingSoc) ? session.startingSoc * 100 : undefined,
        endingSocPercentage: isDefined(session.endingSoc) ? session.endingSoc * 100 : undefined,
        chargerId: session.chargerId,
        chargerName: session.charger,
        connectorName: session.connector,
        connectorId: session.evseId,
        vehicleId: session.evAssetId,
      })),
      siteId: siteId ?? undefined,
      viewAllLink:
        siteId && vehicleId && start && end
          ? {
              routerLink: [RouteSegment.Root, RouteSegment.Sites, siteId, RouteSegment.Chargers, RouteSegment.ChargerSessions],
              queryParams: { start: start, end: end, vehicles: vehicleId },
            }
          : undefined,
    };
  }
);
